/**
 * @file apps/web/src/pages/UatPage.tsx
 * @description UAT cycles/subcycles (test scripts + defects) and sessions
 * for a project. Tabs: Cycles | Sessions. Test scripts and defects live
 * under the Cycles tab's drill-down (UatCycleBrowser) — see that component
 * for their tabs and management. Audio upload lives on DiscoveryPage.tsx
 * now, not here — a workshop recording is a Discovery-phase input (it's
 * what the skill tree gets reviewed against), not a UAT one.
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, ClipboardList, Layers, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { uatApi } from '../lib/api.js';
import { Badge } from '../components/Badge.js';
import { UatCycleBrowser } from '../components/UatCycleBrowser.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UatSession {
  id:          string;
  name:        string;
  status:      string;
  created_at:  string;
  scheduled_at?: string | null;
  started_at?:   string | null;
  completed_at?: string | null;
}

// ---------------------------------------------------------------------------
// Session card
// ---------------------------------------------------------------------------

function SessionCard({
  session,
  onUpdateStatus,
}: {
  session:        UatSession;
  onUpdateStatus: (id: string, status: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const nextStatus: Record<string, string | undefined> = {
    scheduled:   'in_progress',
    in_progress: 'completed',
  };

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-sm text-ink">{session.name}</p>
            <Badge variant={session.status as never} />
          </div>
          <p className="text-[11px] font-mono text-muted mt-0.5">
            {new Date(session.created_at).toLocaleDateString('en-US', {
              month: 'short', day: 'numeric', year: 'numeric',
            })}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {nextStatus[session.status] && (
            <button
              onClick={() => onUpdateStatus(session.id, nextStatus[session.status]!)}
              className="btn-secondary text-xs py-1 px-2"
            >
              Mark {nextStatus[session.status]?.replace('_', ' ')}
            </button>
          )}
          <button
            onClick={() => setExpanded((v) => !v)}
            className="btn-icon text-muted"
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="pt-2 border-t border-[rgba(8,17,31,0.06)] grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono text-muted">
          {session.scheduled_at && (
            <p>Scheduled: <span className="text-ink">{new Date(session.scheduled_at).toLocaleDateString()}</span></p>
          )}
          {session.started_at && (
            <p>Started: <span className="text-ink">{new Date(session.started_at).toLocaleDateString()}</span></p>
          )}
          {session.completed_at && (
            <p>Completed: <span className="text-ink">{new Date(session.completed_at).toLocaleDateString()}</span></p>
          )}
          <p>ID: <span className="text-ink">{session.id.slice(0, 8)}…</span></p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// UatPage
// ---------------------------------------------------------------------------

type ActiveTab = 'cycles' | 'sessions';

export function UatPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { user, devHeaders } = useAuth();

  const [tab, setTab]               = useState<ActiveTab>('cycles');
  const [sessions, setSessions]     = useState<UatSession[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [showNewSession, setShowNewSession] = useState(false);
  const [newSessionName, setNewSessionName] = useState('');

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const sess = await uatApi.listSessions(projectId, devHeaders) as UatSession[];
      setSessions(sess);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load UAT data');
    } finally {
      setLoading(false);
    }
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Create new session
  const handleNewSession = async () => {
    if (!projectId || !newSessionName.trim()) return;
    try {
      const created = await uatApi.createSession({
        project_id:  projectId,
        name:        newSessionName.trim(),
        status:      'scheduled',
        created_by:  user.userId,
      }, devHeaders) as UatSession;
      setSessions((prev) => [...prev, created]);
      setShowNewSession(false);
      setNewSessionName('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create session');
    }
  };

  const handleUpdateSessionStatus = async (id: string, status: string) => {
    setSessions((prev) => prev.map((s) => s.id === id ? { ...s, status } : s));
    try {
      await uatApi.updateSession(id, { status }, devHeaders);
    } catch {
      await load();
    }
  };

  const TABS: { key: ActiveTab; label: string; icon: React.ReactNode; count?: number }[] = [
    { key: 'cycles',   label: 'Cycles',    icon: <Layers size={14} /> },
    { key: 'sessions', label: 'Sessions',  icon: <ClipboardList size={14} />, count: sessions.length },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold text-ink">UAT</h2>
        {tab === 'sessions' && (
          <button onClick={() => setShowNewSession((v) => !v)} className="btn-primary text-sm">
            <Plus size={14} /> New Session
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-[rgba(8,17,31,0.08)]">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={[
              'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === t.key
                ? 'border-blue text-blue'
                : 'border-transparent text-muted hover:text-ink',
            ].join(' ')}
          >
            {t.icon}
            {t.label}
            {t.count != null && t.count > 0 && (
              <span className={[
                'text-[10px] font-mono px-1.5 py-0.5 rounded-full',
                tab === t.key ? 'bg-blue/10 text-blue' : 'bg-gray text-muted',
              ].join(' ')}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Cycles tab — Project -> UAT Cycle -> UAT Subcycle -> Test Scripts */}
      {tab === 'cycles' && projectId && (
        <UatCycleBrowser projectId={projectId} devHeaders={devHeaders} />
      )}

      {/* Sessions tab */}
      {tab === 'sessions' && (
        <div className="space-y-3">
          {showNewSession && (
            <div className="card p-4 space-y-3">
              <p className="text-sm font-medium text-ink">New UAT Session</p>
              <input
                type="text"
                value={newSessionName}
                onChange={(e) => setNewSessionName(e.target.value)}
                placeholder='e.g. "Sprint 3 UAT"'
                className="input"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') void handleNewSession(); if (e.key === 'Escape') { setShowNewSession(false); setNewSessionName(''); } }}
              />
              <div className="flex gap-2 justify-end">
                <button onClick={() => { setShowNewSession(false); setNewSessionName(''); }} className="btn-ghost text-sm">Cancel</button>
                <button onClick={() => void handleNewSession()} disabled={!newSessionName.trim()} className="btn-primary text-sm">Create Session</button>
              </div>
            </div>
          )}
          {loading ? (
            <div className="space-y-3 animate-pulse">
              {[1,2,3].map((i) => <div key={i} className="card h-16 bg-gray/60" />)}
            </div>
          ) : sessions.length === 0 ? (
            <div className="empty-state py-12">
              <ClipboardList size={24} className="text-muted/40 mb-2 mx-auto" />
              <p className="text-sm text-muted">No UAT sessions yet.</p>
              <button onClick={() => setShowNewSession(true)} className="btn-secondary mt-4">
                <Plus size={14} /> New Session
              </button>
            </div>
          ) : (
            sessions.map((s) => (
              <SessionCard
                key={s.id}
                session={s}
                onUpdateStatus={handleUpdateSessionStatus}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}
